export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

/** Construct only from a verified server session, never from an incoming payload. */
export interface Actor {
  id: string;
  workspaceId: string;
  role: 'owner' | 'agent' | 'runner' | 'gateway';
}

export interface PolicyIssue {
  code: string;
  message: string;
  field?: string;
}

export interface PolicyDecision {
  allowed: boolean;
  issues: PolicyIssue[];
}

export class PolicyError extends Error {
  readonly issues: PolicyIssue[];
  constructor(issues: PolicyIssue[]) {
    super(issues.map((issue) => issue.message).join('; '));
    this.name = 'PolicyError';
    this.issues = issues;
  }
}

export type ActionKind = 'publication' | 'message' | 'campaign_change' | 'campaign_create' | 'campaign_activate';
export type ActionState = 'prepared' | 'awaiting_approval' | 'approved' | 'dispatching' | 'provider_accepted' | 'confirmed' | 'failed' | 'outcome_unknown' | 'cancelled';
export type FidelityVerdict = 'pass' | 'fail' | 'unable_to_verify' | 'not_depicted';

export interface FidelityFinding {
  feature: string;
  verdict: FidelityVerdict;
  required: boolean;
  evidenceIds: string[];
  reason: string;
}

export interface FidelityReview {
  id: string;
  workspaceId: string;
  exportId: string;
  exportHash: string;
  referenceVersionId: string;
  reviewerId: string;
  creatorId: string;
  independentContext: boolean;
  verdict: FidelityVerdict;
  scope: string;
  findings: FidelityFinding[];
  mediaType: 'image' | 'video' | 'sequence';
  reviewedWholeOutput: boolean;
  completedAt: string;
  superseded: boolean;
}

export interface AssetEvidence {
  assetId: string;
  workspaceId: string;
  exportHash: string;
  currentHash: string | null;
  referenceVersionId: string;
  currentReferenceVersionId: string;
  available: boolean;
  requiresFidelity: boolean;
  fidelityReview: FidelityReview | null;
  ownerReviewed: boolean;
  rights: {
    verified: boolean;
    validFrom: string | null;
    expiresAt: string | null;
    allowedPlacements: string[];
    paidUsage: boolean;
  };
}

export interface ClaimEvidence {
  id: string;
  status: 'verified' | 'unknown' | 'contradicted';
  checkedAt: string;
  validUntil: string;
  sourceIds: string[];
  sourceInspected: boolean;
  challengedOrRecomputed: boolean;
  finalOutputChecked: boolean;
}

export interface ExternalAction {
  id: string;
  workspaceId: string;
  kind: ActionKind;
  state: ActionState;
  revision: number;
  payloadHash: string;
  accountId: string;
  placement: string;
  productVariantId: string | null;
  isPaid: boolean;
  identityDeparture: boolean;
  assets: AssetEvidence[];
  claims: ClaimEvidence[];
  /** Snapshot hashes are opaque evidence of current provider settings or thread state. */
  expectedProviderStateHash: string | null;
  expectedThreadRevision: string | null;
}

export interface Approval {
  schemaVersion: 1;
  id: string;
  workspaceId: string;
  actionId: string;
  actionKind: ActionKind;
  actionRevision: number;
  payloadHash: string;
  accountId: string;
  placement: string;
  productVariantId: string | null;
  approvedBy: string;
  approvedByRole: 'owner';
  approvedAt: string;
  notBefore: string;
  expiresAt: string;
  revokedAt: string | null;
  fidelityReviewIds: string[];
  identityChangeApproved: boolean;
}

export interface DispatchContext {
  now: string;
  actor: Actor;
  executionPaused: boolean;
  connectionVerified: boolean;
  connectedAccountId: string;
  currentProviderStateHash: string | null;
  currentThreadRevision: string | null;
  currentPayloadHash: string;
  restoredExecutionLocked: boolean;
}
